import React, { useEffect, useState, useRef, memo, useContext } from "react";
import { StyledContainer } from "./styles";
import Draggable from "react-draggable";
import classnames from "classnames";
import { Resizable } from "react-resizable";
import { roundOffTo } from "../../../Utils";
import FloatingMenu from "../FloatingMenu";
import { CardContext } from "..";
import useMeasure from "react-use-measure";
import { ResizeObserver } from '@juggle/resize-observer';
import { mergeRefs } from "react-merge-refs";
import { DESELECT_ACTION, SET_POSITION_ACTION, SET_SIZE_ACTION } from "../hooks/useCanvasReducer";

// export const GRID = [30, 30]; // grid to for dragging to snap to
const RESIZE_GRID = [30, 30]; // grid for resizing
const RESIZE_WIDTH_BOUNDS = [120, 900];
const RESIZE_HEIGHT_BOUNDS = [120, 900];
export const DEFAULT_CARD_SIZE = [330, 240];
const GROUP_SELECT_ZINDEX_DEPRIORITIZATION = 4000000;

export function roundSize(length) {
  return roundOffTo(length, RESIZE_GRID[0]);
}

function useTraceUpdate(id, props) {
  const prev = useRef(props);
  useEffect(() => {
    const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
      if (prev.current[k] !== v) {
        ps[k] = [prev.current[k], v];
      }
      return ps;
    }, {});
    if (Object.keys(changedProps).length > 0) {
      console.log(`Changed props [${id}]:`, changedProps);
    }
    prev.current = props;
  });
}

function Card({
  x,
  y,
  content,
  width,
  height,
  id,
  selected,
  zoom,
  generating,
  index,
  onEditIntent,
  lockAspectRatio = false,
  generatable = true,
  children,
  naked = false,
  autoHeight = false, // whether the height should resize to contain the content
  resizable = true,
  minSize = null,
  generateCTAText,
}) {
  const {
    selectedCardIds, //
    deselectAllCards,
    isSpaceDragging,
    setPosition,
    setAutoHeight, // sets autoheight for a card
    addGeneratedCard,
    addImageCard,
    selectAdditionalCard,
    isSpaceDown,
    debug,
    readOnly = false,
    onDragEnd,
    onDragging,
    dispatch,
  } = useContext(CardContext);

  const clicksSinceSelect = useRef(0);

  // // true if this is the only card selected
  const onlyThisSelected = selected && selectedCardIds.length === 1;

  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false); // whether the card is being resized
  const [showShadow, setShowShadow] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [disablePlus, setDisablePlus] = useState(false); // disabling plus, used for when generation hasn't completed yet

  const prevPosition = useRef({ x: x, y: y });
  const [contentRef, { height: contentHeight }] = useMeasure({ polyfill: ResizeObserver });
  const contentManualRef = useRef(null);

  // upon selection logic
  useEffect(() => {
    // only show menu if user selected just this card
    setShowMenu(onlyThisSelected);
  }, [selected, selectedCardIds]);

  useEffect(() => {
    if (!selected) {
      clicksSinceSelect.current = 0;
    }
  }, [selected]);

  // on auto height, set height dynamically based on size of child content
  useEffect(() => {
    if (autoHeight) {
      const newWidth = width;
      const newHeight = contentManualRef.current.offsetHeight; // we use offset height here because contentHeight changes with zoom due to scale. So, we use contentHeight to tell us that the height has changed, but use offset height for the actual number

      // console.log(contentManualRef.current.offsetHeight);

      if (newHeight !== height) {
        // console.log("here");
        dispatch({ type: SET_SIZE_ACTION, id: id, width: newWidth, height: newHeight });
      }
    }
  }, [autoHeight, contentHeight, width]);

  const addGenerated = () => {
    addGeneratedCard({
      ids: [id],
      y: y,
      x: x + width + 105,
      width: DEFAULT_CARD_SIZE[0],
      height: DEFAULT_CARD_SIZE[1],
      sourceCardIds: [id],
    });
    dispatch({ type: DESELECT_ACTION, id: id });
    // setSelected([id], false); // unselect this card

    setDisablePlus(true);
    setShowShadow(false);

    setTimeout(() => {
      setDisablePlus(false);
    }, 1000);
  };

  const addImage = () => {
    addImageCard({
      y: y,
      x: x + width + 105,
      prompt: content,
      sourceCardIds: [id],
    });
    dispatch({ type: DESELECT_ACTION, id: id });
    // setSelected([id], false); // unselect this card
  };

  // if multiple cards are selected, we don't border the box (the group selection bounding box will show instead)
  const shouldShowSelectionBorder = selected && selectedCardIds.length === 1;

  let zIndex;
  if (selectedCardIds.length > 1 && !selected) {
    // if a group is selected and we're not in the group, then move it to a deprioritized layer below
    // TODO: this might not work if distance is vast between groups of cards, but should be ok most of the time
    zIndex = index - GROUP_SELECT_ZINDEX_DEPRIORITIZATION;
  } else {
    zIndex = index;
  }

  return (
    <StyledContainer
      data-disableselect={true}
      className={classnames({
        selected: selected,
        card: true,
        "is-space-dragging": isSpaceDragging,
        naked: naked,
      })}
      data-id={id}
      style={{
        zIndex: zIndex,
      }}
      // onClick={onClick ? onClick : null}
    >
      <Draggable
        disabled={isSpaceDown || readOnly}
        position={{ x: x, y: y }}
        onStart={(e, data) => {
          setDragging(true);

          // guard against moving while resizing
          if (e.target.closest(".react-resizable-handle") || e.target.closest(".picker") || e.target.closest(".no-drag")) {
            return false;
          }

          // if (!selectedCardIds.includes(id) && !(e.metaKey || e.shiftKey)) {
          if (e.metaKey || e.shiftKey) {
            // if meta key down and we're starting to drag, then select the add'l card
            selectAdditionalCard({ id: id });
          } else if (!selectedCardIds.includes(id)) {
            // if there is a currently selected group but this card isn't in it, then deselect it
            // console.log("deselecting cards from onstart");
            deselectAllCards({ exceptId: id });
          }

          prevPosition.current = { x: data.x, y: data.y };
        }}
        onStop={(e, data) => {
          setDragging(false);
          onDragEnd(id);

          const TOLERANCE = 2;

          // increment clicks
          clicksSinceSelect.current++;

          if (e.metaKey || e.shiftKey) {
            // selectAdditionalCard({ id: id });
          } else {
            const dX = Math.abs(prevPosition.current.x - data.x);
            const dY = Math.abs(prevPosition.current.y - data.y);
            // when clicking on card without dragging, select it and deselect all other cards
            if (dX < TOLERANCE && dY < TOLERANCE) {
              if (clicksSinceSelect.current >= 2) {
                // if user clicks more than 1 time, then they have an intent to edit
                if (onEditIntent) {
                  onEditIntent();
                }
              }
            }
          }
        }}
        onDrag={(e, data) => {
          dispatch({ type: SET_POSITION_ACTION, id: id, deltaX: data.deltaX, deltaY: data.deltaY });
          // setPosition(id, data.deltaX, data.deltaY);
          onDragging({ id, deltaX: data.deltaX, deltaY: data.deltaY });
        }}
        cancel=".edit-on"
        // grid={GRID}
        scale={zoom}
      >
        <Resizable
          height={height || 0}
          width={width}
          axis={autoHeight ? "x" : "both"}
          // these size constraints do NOT apply when the card is autoHeight
          minConstraints={[60, 60]}
          maxConstraints={[900, 900]}
          onResizeStart={(e) => {
            setResizing(true);
          }}
          onResizeStop={(e) => setResizing(false)}
          resizeHandles={selectedCardIds.length > 1 || isSpaceDown || !resizable ? [] : ["s", "w", "e", "n", "sw", "nw", "se", "ne"]}
          onResize={(e, { element, size, handle }) => {
            // do not allow resizing when group select
            if (selectedCardIds.length > 1) {
              return;
            }
            // Calculate new size based on movement since this is easier to account for zoom
            // console.log("x", e.movementX);
            // console.log("y", e.movementY);

            // Change in width and height
            let dWidth = 0;
            let dHeight = 0;
            let dX = 0;
            let dY = 0;

            // keep track of whether we need to reposition the card since resizing is occuring on left/top edge
            let requiresRepositionX = false;
            let requiresRepositionY = false;

            // Only allow width movement on left or right edge
            if (["w", "sw", "nw", "e", "se", "ne"].includes(handle)) {
              dWidth = (e.movementX * 1) / zoom;
            }

            // Only allow height movement on top or bottom edge
            if (["n", "nw", "ne", "s", "sw", "se"].includes(handle)) {
              dHeight = (e.movementY * 1) / zoom;
            }

            // On left edge, reverse polarity and reposition width
            if (["sw", "w", "nw"].includes(handle)) {
              dWidth = dWidth * -1;
              requiresRepositionX = true;
            }

            // On right edge, reverse polarity and reposition height
            if (["nw", "n", "ne"].includes(handle)) {
              dHeight = dHeight * -1;
              requiresRepositionY = true;
            }

            // calculate hew width/height railing against bounds
            const newWidth = Math.min(Math.max(width + dWidth, RESIZE_WIDTH_BOUNDS[0]), RESIZE_WIDTH_BOUNDS[1]);
            let newHeight = Math.min(Math.max(height + dHeight, RESIZE_HEIGHT_BOUNDS[0]), RESIZE_HEIGHT_BOUNDS[1]);

            // lock aspect ratio for images
            if (lockAspectRatio) {
              newHeight = newWidth;
            }

            // If reposition is required, we reposition ONLY if we've reached the grid boundary
            // or the card may move before the rounded rendered size has changed
            if (requiresRepositionX && roundSize(newWidth) !== roundSize(width)) {
              dX = roundSize(width) - roundSize(newWidth);
            }
            if (requiresRepositionY && roundSize(newHeight) !== roundSize(height)) {
              dY = roundSize(height) - roundSize(newHeight);
            }

            // sets size taking zoom into account using delta movement
            dispatch({ type: SET_SIZE_ACTION, id: id, width: newWidth, height: newHeight, autoHeight: false });

            // reposition only if necessary (when resizing inward from the left or top)
            if (dY !== 0 || dX !== 0) {
              // setPosition(id, dX, dY);
              dispatch({ type: SET_POSITION_ACTION, id: id, deltaX: dX, deltaY: dY });
            }
          }}
        >
          <div
            // round width and height to grid
            style={{
              width: `${roundSize(width)}px`,
              height: autoHeight ? null : `${roundSize(height)}px`, // if auto height, we let children content dictate the height
              minHeight: minSize ? minSize[1] : null,
            }}
            className={classnames("inner", { selected: shouldShowSelectionBorder })}
            ref={mergeRefs([contentRef, contentManualRef])}
          >
            {!resizing && !disablePlus && !selected && generatable && !isSpaceDown && (
              <div className="plus">
                <a
                  className="inner"
                  onMouseOver={(e) => setShowShadow(true)}
                  onMouseOut={(e) => setShowShadow(false)}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    addGenerated();
                  }}
                >
                  +
                </a>
              </div>
            )}

            {showShadow && (
              <>
                <Shadow
                  width={width}
                  height={height}
                />
              </>
            )}

            {showMenu && generatable && !isSpaceDown && !generating && selectedCardIds.length === 1 && (
              <FloatingMenu
                generateCTAText={generateCTAText}
                addGenerated={addGenerated} //
                addImage={addImage}
                zoom={zoom}
              />
            )}

            {children}

            {debug && (
              <div className="debug">
                <div>id: {id}</div>
                <div>x: {Math.round(x)}</div>
                <div>y: {Math.round(y)}</div>
                <div>w: {Math.round(width)}</div>
                <div>h: {Math.round(height)}</div>
                <div>selected: {selected ? "true" : "false"} </div>
                <div>contentHeight: {Math.round(contentHeight)}</div>
                <div>autoHeight: {autoHeight ? "true" : "false"}</div>
              </div>
            )}
          </div>
        </Resizable>
      </Draggable>
    </StyledContainer>
  );
}

function Shadow({ width, height }) {
  return (
    <>
      <div
        className="shadow"
        style={{
          width: `${DEFAULT_CARD_SIZE[0]}px`,
          height: `${DEFAULT_CARD_SIZE[1]}px`,
        }}
      >
        <div>Generate more like this</div>
      </div>
      <div
        className="shadow"
        style={{
          width: `${DEFAULT_CARD_SIZE[0]}px`,
          height: `${DEFAULT_CARD_SIZE[1]}px`,
          top: -DEFAULT_CARD_SIZE[1] - 50 + "px",
        }}
      >
        {/* <div>Generate more like this</div> */}
      </div>
      <div
        className="shadow"
        style={{
          width: `${DEFAULT_CARD_SIZE[0]}px`,
          height: `${DEFAULT_CARD_SIZE[1]}px`,
          top: DEFAULT_CARD_SIZE[1] + 50 + "px",
        }}
      >
        {/* <div>Generate more like this</div> */}
      </div>
    </>
  );
}

export default memo(Card);
